import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAlert } from 'react-alert';

import { createDiscount } from '../../../actions/discountActions';
import { CREATE_DISCOUNT_RESET } from '../../../constants/discountConstants';
import Loader from '../../Loader';

const AddDiscount = ({ history }) => {
  const dispatch = useDispatch();
  const alert = useAlert();

  const [form, setForm] = useState({
    code: '',
    name: '',
    type: 'percentage',
    value: '',
    validFrom: '',
    validUntil: '',
    usageLimit: '',
    description: '',
    status: true,
  });

  const { loading, success, error, errors } = useSelector((state) => state.discountCreate);

  useEffect(() => {
    if (error) alert.error(error);
    if (errors && typeof errors === 'object') {
      // show first error if available
      const first = Object.values(errors)[0];
      if (first) alert.error(Array.isArray(first) ? first[0] : first);
    }
    if (success) {
      alert.success('Discount created successfully');
      dispatch({ type: CREATE_DISCOUNT_RESET });
      history.push('/admin/discounts');
    }
  }, [error, errors, success, alert, dispatch, history]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const submitHandler = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      value: Number(form.value),
      usageLimit: form.usageLimit ? Number(form.usageLimit) : undefined,
    };
    dispatch(createDiscount(payload));
  };

  return (
    <Fragment>
      {loading ? (
        <Loader />
      ) : (
        <div className="content-wrapper">
          <section className="content-header">
            <h1>
              Add <small>Discount</small>
            </h1>
            <ol className="breadcrumb">
              <li>
                <Link to="/admin">
                  <i className="fa fa-dashboard"></i> Dashboard
                </Link>
              </li>
              <li>
                <Link to="/admin/discounts">
                  <i className="fa fa-tags"></i> Discounts
                </Link>
              </li>
              <li className="active">Add Discount</li>
            </ol>
          </section>

          <section className="content">
            <div className="box box-default">
              <div className="box-header with-border">
                <h3 className="box-title">Add Discount</h3>
              </div>
              <form id="quickForm" onSubmit={submitHandler}>
                <div className="box-body">
                  <div className="form-group row">
                    <div className="col-md-6">
                      <label htmlFor="code">Code / Referral Code</label>
                      <input
                        type="text"
                        name="code"
                        className="form-control"
                        id="code"
                        placeholder="e.g. OTS2025 or JOHNDOE50"
                        value={form.code}
                        onChange={onChange}
                        required
                      />
                      {form.code ? (
                        <div style={{ marginTop: 8 }}>
                          <small>Registration referral link</small>
                          <div className="input-group">
                            <input
                              type="text"
                              readOnly
                              className="form-control"
                              value={`${(process.env.REACT_APP_LANDING_BASE_URL || (typeof window !== 'undefined' && window.location && window.location.origin) || '')}/landing/register?ref=${encodeURIComponent(form.code)}`}
                            />
                            <span className="input-group-btn">
                              <button
                                type="button"
                                className="btn btn-default"
                                onClick={() => {
                                  const link = `${(process.env.REACT_APP_LANDING_BASE_URL || (typeof window !== 'undefined' && window.location && window.location.origin) || '')}/landing/register?ref=${encodeURIComponent(form.code)}`;
                                  navigator.clipboard?.writeText(link);
                                  alert.success('Referral link copied');
                                }}
                              >
                                Copy
                              </button>
                            </span>
                          </div>
                          <small style={{ display: 'block', marginTop: 8 }}>Apply-discount link (plans)</small>
                          <div className="input-group">
                            <input
                              type="text"
                              readOnly
                              className="form-control"
                              value={`${window.location.origin}/subscription-plans?code=${encodeURIComponent(form.code)}`}
                            />
                            <span className="input-group-btn">
                              <button
                                type="button"
                                className="btn btn-default"
                                onClick={() => {
                                  const link = `${window.location.origin}/subscription-plans?code=${encodeURIComponent(form.code)}`;
                                  navigator.clipboard?.writeText(link);
                                  alert.success('Plans link copied');
                                }}
                              >
                                Copy
                              </button>
                            </span>
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="name">Name</label>
                      <input type="text" name="name" className="form-control" id="name" value={form.name} onChange={onChange} required />
                    </div>
                  </div>

                  <div className="form-group row">
                    <div className="col-md-12">
                      <label htmlFor="description">Description</label>
                      <textarea
                        name="description"
                        id="description"
                        className="form-control"
                        placeholder="Short description of the discount (required)"
                        value={form.description}
                        onChange={onChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group row">
                    <div className="col-md-4">
                      <label htmlFor="type">Type</label>
                      <select name="type" className="form-control" id="type" value={form.type} onChange={onChange}>
                        <option value="percentage">Percentage</option>
                        <option value="fixed">Fixed</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="value">Value</label>
                      <input type="number" step="0.01" name="value" className="form-control" id="value" value={form.value} onChange={onChange} required />
                    </div>
                    <div className="col-md-4">
                      <label className="form-check-label" htmlFor="status">Active</label>
                      <div className="form-check">
                        <input style={{ marginRight: '5px' }} type="checkbox" className="form-check-input" name="status" id="status" checked={form.status} onChange={onChange} />
                      </div>
                    </div>
                  </div>

                  <div className="form-group row">
                    <div className="col-md-6">
                      <label htmlFor="validFrom">Valid From</label>
                      <input type="date" name="validFrom" className="form-control" id="validFrom" value={form.validFrom} onChange={onChange} required />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="validUntil">Valid Until</label>
                      <input type="date" name="validUntil" className="form-control" id="validUntil" value={form.validUntil} onChange={onChange} required />
                    </div>
                  </div>

                  <div className="form-group row">
                    <div className="col-md-4">
                      <label htmlFor="usageLimit">Usage Limit (optional)</label>
                      <input type="number" name="usageLimit" className="form-control" id="usageLimit" value={form.usageLimit} onChange={onChange} placeholder="Leave empty for unlimited" />
                      <small className="help-block">Leave empty for unlimited usage.</small>
                    </div>
                  </div>
                </div>
                <div className="box-footer">
                  <button type="submit" className="btn btn-primary">Submit</button>
                </div>
              </form>
            </div>
          </section>
        </div>
      )}
    </Fragment>
  );
};

export default AddDiscount;
